import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import HomePage from "./pages/HomePage";
import ProductsPage from "./pages/ProductsPage";
import ProductDetailPage from "./pages/ProductDetailPage";
import PreOwnedPage from "./pages/PreOwnedPage";
import MembershipPage from "./pages/MembershipPage";
import TripsPage from "./pages/TripsPage";
import TripDetailPage from "./pages/TripDetailPage";
import CartPage from "./pages/CartPage";
import AuthPage from "./pages/AuthPage";
import DashboardPage from "./pages/DashboardPage";
import AdminPage from "./pages/AdminPage";
import PaymentInstructionsPage from "./pages/PaymentInstructionsPage";
import PaymentSuccessPage from "./pages/PaymentSuccessPage";
import PaymentCancelledPage from "./pages/PaymentCancelledPage";
import TermsPage from "./pages/TermsPage";
import FAQPage from "./pages/FAQPage";
import CoursesPage from "./pages/CoursesPage";
import TorchesPage from "./pages/TorchesPage";
import EventsPage from "./pages/EventsPage";
import MedicalQuestionnairePage from "./pages/MedicalQuestionnairePage";
import LiabilityWaiverPage from "./pages/LiabilityWaiverPage";
import BrandPage from "./pages/BrandPage";
import BrandProductPage from "./pages/BrandProductPage";
import UnsubscribePage from "./pages/UnsubscribePage";
import JoinPage from "./pages/JoinPage";
import PlannerHome from "./pages/planner/PlannerHome";
import TripDetail from "./pages/planner/TripDetail";
import DayPlanner from "./pages/planner/DayPlanner";
import PlannerShare from "./pages/planner/PlannerShare";
import PlannerManual from "./pages/planner/PlannerManual";
import PlannerViewer from "./pages/planner/PlannerViewer";
import PlannerMyDives from "./pages/planner/PlannerMyDives";
import ErrorBoundary from "./components/ErrorBoundary";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { CartProvider } from "./context/CartContext";
import { usePageTracking } from "./hooks/useAnalytics";
import "@/App.css";

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  // Wait for auth check to complete before redirecting
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0A2342]"></div>
      </div>
    );
  }
  
  return user ? children : <Navigate to="/auth" />;
};

function AppContent() {
  // Track page visits for analytics
  usePageTracking();
  
  return (
    <div className="App flex flex-col min-h-screen">
      <Navbar />
      <main className="flex-grow">
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/products" element={<ProductsPage />} />
          <Route path="/products/:id" element={<ProductDetailPage />} />
          <Route path="/pre-owned" element={<PreOwnedPage />} />
          <Route path="/membership" element={<MembershipPage />} />
          <Route path="/trips" element={<TripsPage />} />
          <Route path="/trips/:tripId" element={<TripDetailPage />} />
          <Route path="/events" element={<EventsPage />} />
          <Route path="/cart" element={<CartPage />} />
          <Route path="/auth" element={<AuthPage />} />
          <Route path="/payment-instructions" element={<PaymentInstructionsPage />} />
          <Route 
            path="/dashboard" 
            element={
              <ProtectedRoute>
                <DashboardPage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/admin" 
            element={
              <ProtectedRoute>
                <AdminPage />
              </ProtectedRoute>
            } 
          />
          <Route path="/payment-success" element={<PaymentSuccessPage />} />
          <Route path="/payment-cancelled" element={<PaymentCancelledPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/faq" element={<FAQPage />} />
          <Route path="/courses" element={<CoursesPage />} />
          <Route path="/torches" element={<TorchesPage />} />
          <Route path="/medical-questionnaire" element={<MedicalQuestionnairePage />} />
          <Route path="/liability-waiver" element={<LiabilityWaiverPage />} />
          <Route path="/brand/:slug" element={<BrandPage />} />
          <Route path="/brand-product/:productId" element={<BrandProductPage />} />
          <Route path="/unsubscribe" element={<UnsubscribePage />} />
          <Route path="/join" element={<JoinPage />} />
          <Route path="/planner" element={<ErrorBoundary><PlannerHome /></ErrorBoundary>} />
          <Route path="/planner/trips/:tripId" element={<ErrorBoundary><TripDetail /></ErrorBoundary>} />
          <Route path="/planner/trips/:tripId/day/:planDate" element={<ErrorBoundary><DayPlanner /></ErrorBoundary>} />
          <Route path="/planner/share/:token" element={<ErrorBoundary><PlannerShare /></ErrorBoundary>} />
          <Route path="/planner/viewer" element={<ErrorBoundary><PlannerViewer /></ErrorBoundary>} />
          <Route path="/planner/my-dives" element={<ErrorBoundary><PlannerMyDives /></ErrorBoundary>} />
          <Route path="/planner/manual" element={<ErrorBoundary><PlannerManual /></ErrorBoundary>} />
        </Routes>
      </main>
      <Footer />
      <Toaster position="top-center" />
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <CartProvider>
          <AppContent />
        </CartProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;